import { trackProgress } from "~/lib/brobsProgress";
import { mountMask } from "~/parent/mask";

const isDev = import.meta.env.DEV;
const outputPath = isDev ? "dev" : "dist";
const cssOutputPath = "dist";

/**
 * Loads the VS Code workbench in ESM mode inside a mask in `root`. Call it one time for each page
 * load: the workbench attaches to its container when it loads, and cannot move.
 */
export function mountVsCode(root: HTMLElement): void {
  const progress = trackProgress("vscode");
  progress("mounted");

  const { root: maskRoot, content } = mountMask(root, "vscode_editor");

  const builtinExtensions = document.createElement("meta");
  builtinExtensions.id = "vscode-workbench-builtin-extensions";
  builtinExtensions.dataset.settings = "[]";

  const webConfiguration = document.createElement("meta");
  webConfiguration.id = "vscode-workbench-web-configuration";
  webConfiguration.dataset.settings = "{}";

  const mainCss = document.createElement("link");
  mainCss.dataset.name = "vs/workbench/workbench.web.main";
  mainCss.rel = "stylesheet";
  mainCss.href = `/${cssOutputPath}/out/vs/workbench/workbench.web.main.css`;

  // In dev, each CSS import of a workbench module becomes a call to `_VSCODE_CSS_LOAD` (see the
  // vscode-esm-css-loader plugin). The call adds an `@import` rule to this style sheet.
  const cssModules = document.createElement("style");
  cssModules.id = "vscode-css-modules";
  cssModules.media = "screen";

  const container = document.createElement("div");
  Object.assign(container.style, {
    width: "100%",
    height: "100%",
    overflow: "hidden",
  });

  content.append(
    builtinExtensions,
    webConfiguration,
    mainCss,
    cssModules,
    container,
  );

  const sheet = cssModules.sheet!;
  globalThis._VSCODE_CSS_LOAD = (url: string) => {
    try {
      sheet.insertRule(`@import url("${url}");`, sheet.cssRules.length);
    } catch (error) {
      console.error("Failed to load CSS module:", url, error);
    }
  };
  progress("css loader");

  globalThis._VSCODE_NLS_LANGUAGE ??= "en";

  // Needed for VSCode ESM
  globalThis._VSCODE_FILE_ROOT = window.location.origin + `/${outputPath}/out`;

  // The workbench reads its container from this global when it loads.
  window.vscodeTargetContainer = container;

  const workbenchUrl = `/${outputPath}/out/vs/code/browser/workbench/workbench.js`;

  const describeLoadFailure = async (event: Event | string) => {
    try {
      const response = await fetch(workbenchUrl, { method: "HEAD" });
      console.error("Failed to load workbench script:", {
        event,
        workbenchUrl,
        status: response.status,
        contentType: response.headers.get("content-type"),
        hint:
          response.status === 404
            ? "vscode/out is missing or watch-client is still compiling — run `gulp compile-client` in vscode/"
            : response.headers.get("content-type")?.includes("text/html")
              ? "Got HTML instead of JS — check public/dev symlink and vite static serving"
              : undefined,
      });
    } catch (error) {
      console.error("Failed to load workbench script:", event, error);
    }
  };

  // The production build strips English strings out of the bundle and looks them
  // up by index in _VSCODE_NLS_MESSAGES (populated by nls.messages.js). Without it
  // every localize() call throws "NLS MISSING". The dev compile keeps strings
  // inline and doesn't emit the file, so skip it there.
  const loadNlsMessages = (): Promise<void> => {
    if (isDev) {
      globalThis._VSCODE_NLS_MESSAGES ??= [];
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = `/${outputPath}/out/nls.messages.js`;
      script.onload = () => resolve();
      script.onerror = (error) => {
        console.error("Failed to load nls.messages.js:", error);
        resolve();
      };
      document.head.appendChild(script);
    });
  };

  const injectWorkbench = () => {
    const script = document.createElement("script");
    script.type = "module";
    // In dev, the query string stops the browser from reusing a workbench from its cache.
    script.src = isDev ? `${workbenchUrl}?t=${Date.now()}` : workbenchUrl;
    script.onload = () => progress.done();
    script.onerror = (error) => {
      script.remove();
      void describeLoadFailure(error);
    };
    document.head.appendChild(script);
  };

  // A cheap HEAD tells us whether out/ has the compiled bundle yet. While
  // watch-client (re)compiles, the request 404s; vite also serves index.html
  // (text/html) for unmatched paths, so a JS content-type is the real signal.
  const isWorkbenchReady = async (): Promise<boolean> => {
    try {
      const response = await fetch(workbenchUrl, {
        method: "HEAD",
        cache: "no-store",
      });
      const contentType = response.headers.get("content-type") ?? "";
      return response.ok && !contentType.includes("text/html");
    } catch {
      return false;
    }
  };

  const loadWorkbench = async () => {
    progress("workbench");

    // In dev the bundle can be missing for a while during a full recompile
    // (e.g. after a TypeScript bump invalidates the incremental cache). Poll
    // until it's ready and inject once, instead of re-fetching partial bundles
    // on an escalating backoff — this picks it up within one interval of the
    // compile finishing.
    if (isDev) {
      const POLL_MS = 500;
      const MAX_WAIT_MS = 180_000;
      const start = Date.now();
      let warned = false;
      while (!(await isWorkbenchReady())) {
        if (Date.now() - start > MAX_WAIT_MS) {
          void describeLoadFailure("workbench.js never became ready");
          return;
        }
        if (!warned) {
          warned = true;
          console.warn("Waiting for vscode/out (watch-client compiling)…");
        }
        await new Promise((resolve) => window.setTimeout(resolve, POLL_MS));
      }
    }

    injectWorkbench();
  };

  // Load builtin extensions from dev-extensions.json
  const loadExtensions = async () => {
    try {
      const response = await fetch("/dev-extensions.json");
      if (response.ok) {
        builtinExtensions.dataset.settings = await response.text();
      }
      progress("extensions");
    } catch (error) {
      console.warn("Failed to load dev-extensions.json:", error);
    }

    await loadNlsMessages();
    await loadWorkbench();
  };

  loadExtensions().catch((error) => {
    console.error("Failed to load extensions:", error);
  });

  window.addEventListener("pagehide", () => {
    container.style.opacity = "0";
  });

  // The mask sets `display` on its root each time gaze reports a rect, so a panic
  // uses `visibility` on the mask root to keep the editor hidden.
  window.addEventListener("gaze:panic", () => {
    maskRoot.style.visibility = "hidden";
  });
}
